import { Button, Card, message, Modal, Row, Typography } from 'antd';
import moment from 'moment';
import { useState } from 'react';

import PartialSendTestsModal from '../../components/PartialSendTestsModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import {
  CandidateDetail,
  JobPostingDetail,
  JobPostingId,
  RecruitmentProcessCollection,
} from '../../types';
import styles from './VacancyInsights.module.css';

interface VacancyInsightsProps {
  jobPosting?: JobPostingDetail;
  missingTestsCandidateIdsAllStages?: CandidateDetail['id'][];
  finalizedTestsCandidateIdsAtSubscription?: number;
  recruitmentProcesses?: RecruitmentProcessCollection[];
  dashboardStatus?: RecruitmentProcessCollection['status'];
  canManage?: boolean;
}

const VacancyInsights = ({
  jobPosting,
  missingTestsCandidateIdsAllStages,
  finalizedTestsCandidateIdsAtSubscription,
  recruitmentProcesses,
  dashboardStatus,
  canManage,
}: VacancyInsightsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sendTestsCandidateIds, setSendTestsCandidateIds] = useState<
    CandidateDetail['id'][] | undefined
  >(undefined);
  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const navigate = useTypedNavigate();

  return (
    <>
      <PartialSendTestsModal
        jobPostingId={jobPosting?.id}
        candidateIds={sendTestsCandidateIds ?? []}
        open={!!sendTestsCandidateIds}
        width={800}
        onSuccess={() => {
          message.success('Testes enviados.');
          setSendTestsCandidateIds(undefined);
          setIsModalOpen(false);
        }}
        onCancel={() => setSendTestsCandidateIds(undefined)}
        destroyOnClose
      />
      <Modal
        open={isModalOpen}
        title="Testes não respondidos"
        onCancel={() => setIsModalOpen(false)}
        footer={
          <>
            <Button
              onClick={() =>
                navigate({
                  pathname: '/:tenant/opportunities/:id/analysis/',
                  params: { tenant, id, testCompleteness: 'MISSING', stages: '', status: 'ACTIVE' },
                })
              }
            >
              Ver lista de candidatos
            </Button>
            {canManage && (
              <Button
                type="primary"
                onClick={() => setSendTestsCandidateIds(missingTestsCandidateIdsAllStages)}
              >
                Enviar e-mail
              </Button>
            )}
          </>
        }
      >
        <Typography.Paragraph>
          <strong>{missingTestsCandidateIdsAllStages?.length}</strong> candidatos ainda{' '}
          <Typography.Text underline>não responderam</Typography.Text> aos testes.
          <br />
          Deseja enviar um e-mail lembrete?
        </Typography.Paragraph>
      </Modal>
      <Card>
        <Card.Grid
          hoverable={false}
          className={styles.statusCard}
          style={{ backgroundColor: '#F0F1F2' }}
        >
          <Row justify="center">
            {jobPosting?.finalization_date ? (
              <Typography.Title level={5} style={{ color: '#166889' }}>
                {moment(jobPosting?.created_at).from(jobPosting.finalization_date)}
              </Typography.Title>
            ) : (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                {moment(jobPosting?.created_at).fromNow(true)}
              </Typography.Title>
            )}
          </Row>
          <Row justify="center">
            <Typography.Text type="secondary">em aberto</Typography.Text>
          </Row>
        </Card.Grid>
        <Card.Grid
          hoverable={dashboardStatus !== 'INACTIVE'}
          className={styles.statusCard}
          onClick={() => {
            return dashboardStatus !== 'INACTIVE'
              ? navigate({
                  pathname: '/:tenant/opportunities/:id/analysis/',
                  params: {
                    tenant,
                    id,
                    stages: '',
                    status: dashboardStatus ?? '',
                  },
                })
              : undefined;
          }}
        >
          <Row justify="center">
            {recruitmentProcesses && dashboardStatus !== 'INACTIVE' ? (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                {
                  recruitmentProcesses.filter(({ status }) => {
                    if (dashboardStatus === 'ACTIVE') {
                      return status === 'ACTIVE';
                    }

                    return true; // should show all the candidates in the vacancy, not only the ones in Subscribed Stage
                  }).length
                }
              </Typography.Title>
            ) : (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                -
              </Typography.Title>
            )}
          </Row>
          <Row justify="center">
            {dashboardStatus ? (
              <Typography.Text type="secondary">inscritos</Typography.Text>
            ) : (
              <Typography.Text type="secondary">total de candidatos</Typography.Text>
            )}
          </Row>
        </Card.Grid>
        <Card.Grid
          hoverable={missingTestsCandidateIdsAllStages && dashboardStatus !== 'INACTIVE'}
          className={styles.statusCard}
          onClick={() => {
            if (!missingTestsCandidateIdsAllStages) return undefined;
            return dashboardStatus !== 'INACTIVE' ? setIsModalOpen(true) : undefined;
          }}
        >
          <Row justify="center">
            {missingTestsCandidateIdsAllStages && dashboardStatus !== 'INACTIVE' ? (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                {missingTestsCandidateIdsAllStages.length}
              </Typography.Title>
            ) : (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                -
              </Typography.Title>
            )}
          </Row>
          <Row justify="center">
            <Typography.Text type="secondary" style={{ textAlign: 'center', height: 22 }}>
              pendentes de finalizar os testes
            </Typography.Text>
          </Row>
        </Card.Grid>
        <Card.Grid
          hoverable={
            finalizedTestsCandidateIdsAtSubscription !== undefined && dashboardStatus !== 'INACTIVE'
          }
          className={styles.statusCard}
          onClick={() => {
            if (!finalizedTestsCandidateIdsAtSubscription) return undefined;
            return dashboardStatus !== 'INACTIVE'
              ? navigate({
                  pathname: '/:tenant/opportunities/:id/analysis/',
                  params: {
                    tenant,
                    id,
                    testCompleteness: 'FINALIZED',
                    stages: 'SUBSCRIBED',
                    status: 'ACTIVE',
                  },
                })
              : undefined;
          }}
          data-testid="pending-for-selection"
        >
          <Row justify="center">
            {finalizedTestsCandidateIdsAtSubscription !== undefined &&
            dashboardStatus !== 'INACTIVE' ? (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                {finalizedTestsCandidateIdsAtSubscription}
              </Typography.Title>
            ) : (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                -
              </Typography.Title>
            )}
          </Row>
          <Row justify="center">
            <Typography.Text type="secondary">pendentes de triagem</Typography.Text>
          </Row>
        </Card.Grid>
        <Card.Grid hoverable={false} className={styles.statusCard}>
          <Row justify="center">
            {recruitmentProcesses && dashboardStatus !== 'INACTIVE' ? (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                {
                  recruitmentProcesses.filter(({ stage_type, status }) => {
                    if (dashboardStatus === 'ACTIVE') {
                      return (
                        ['SELECTED', 'CUSTOM', 'APPROVED'].includes(stage_type) &&
                        status === 'ACTIVE'
                      );
                    }

                    return ['SELECTED', 'CUSTOM', 'APPROVED'].includes(stage_type);
                  }).length
                }
              </Typography.Title>
            ) : (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                -
              </Typography.Title>
            )}
          </Row>
          <Row justify="center">
            <Typography.Text type="secondary">em processo</Typography.Text>
          </Row>
        </Card.Grid>
        <Card.Grid
          hoverable={dashboardStatus !== 'ACTIVE'}
          className={styles.statusCard}
          onClick={() => {
            return dashboardStatus !== 'ACTIVE'
              ? navigate({
                  pathname: '/:tenant/opportunities/:id/analysis/',
                  params: { tenant, id, stages: '', status: 'INACTIVE' },
                })
              : undefined;
          }}
        >
          <Row justify="center">
            {recruitmentProcesses && dashboardStatus !== 'ACTIVE' ? (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                {
                  recruitmentProcesses.filter(({ status }) =>
                    ['INACTIVE', 'MOVED', 'REMOVED', 'GIVENUP', 'ELIMINATED'].includes(status)
                  ).length
                }
              </Typography.Title>
            ) : (
              <Typography.Title level={3} style={{ color: '#166889' }}>
                -
              </Typography.Title>
            )}
          </Row>
          <Row justify="center">
            <Typography.Text type="secondary">reprovados</Typography.Text>
          </Row>
        </Card.Grid>
      </Card>
    </>
  );
};

export default VacancyInsights;
